import { useState, useEffect } from "react";
import { Search, Grid, List } from "lucide-react";
import { BackgroundSelector } from "../BackgroundSelector";
import { useExhibitors } from "../../hooks/useExhibitors";
import { useEventContext } from "../../contexts/EventContext";
import { Exhibitor } from "../../types/exhibitor.types";
import { useEventDetails } from '../../../hooks/useEventDetails';
import { PortalModal } from "../PortalModal";
import { exhibitorsApi } from "../../api/exhibitors";

// Import common components
import {
  ExpoGrid,
  CategoryFilters,
  ExpoDetailsModal,
  ExpoLoadingState
} from "@portal/components/event_common";

export const ExpoPageV2 = () => {
  const { eventSlug } = useEventContext();
  const { data: exhibitorsResponse, isLoading, error } = useExhibitors(eventSlug);
  const { data: eventDetails } = useEventDetails(eventSlug);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedExhibitor, setSelectedExhibitor] = useState<Exhibitor | null>(null);
  const [background, setBackground] = useState("bg-gray-50");

  // Update page title with custom navigation name
  useEffect(() => {
    if (eventDetails) {
      const expoName = eventDetails.eventOption?.expoName || 'Expo';
      document.title = `${expoName} - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  const exhibitors = exhibitorsResponse?.data.exhibitors || [];
  const categories = exhibitorsResponse?.data.categories || [];

  if (isLoading) {
    return (
      <div className={`min-h-[calc(100vh-64px)] ${background}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <ExpoLoadingState />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-[calc(100vh-64px)] ${background}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-center py-12">
              <p className="text-red-600">Failed to load expo data. Please try again later.</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredExhibitors = exhibitors.filter(exhibitor => {
    const companyName = exhibitor.companyName || exhibitor.name;
    const matchesSearch =
      companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exhibitor.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Convert selected categories to strings for consistent comparison
    const selectedCategoryStrings = selectedCategories.map(id => String(id));
    
    const matchesCategory =
      selectedCategories.length === 0 ||
      (exhibitor.universalCategories && exhibitor.universalCategories.some(cat => 
        selectedCategoryStrings.includes(String(cat.id))
      )) ||
      (exhibitor.category && selectedCategoryStrings.includes(String(exhibitor.category))); // Support legacy category filtering
    return matchesSearch && matchesCategory;
  });

  const handleExhibitorClick = async (exhibitor: Exhibitor, isRefresh = false) => {
    // Fetch the latest exhibitor data including CTA tracking status
    try {
      const response = await exhibitorsApi.getExhibitor(eventSlug, exhibitor.id, isRefresh);
      setSelectedExhibitor(response.data);
    } catch (error) {
      console.error('Error fetching exhibitor details:', error);
      // Fallback to the exhibitor data we already have
      setSelectedExhibitor(exhibitor);
    }
  };

  return (
    <div className={`min-h-[calc(100vh-64px)]`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Expo Hall</h1>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-[50%] -translate-y-[50%] text-gray-400 pointer-events-none"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search exhibitors..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg ${viewMode === "grid" ? "bg-blue-100 text-blue-600" : "text-gray-400"}`}
                  aria-label="Grid view"
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg ${viewMode === "list" ? "bg-blue-100 text-blue-600" : "text-gray-400"}`}
                  aria-label="List view"
                >
                  <List size={20} />
                </button>
              </div>
            </div>

            <CategoryFilters
              categories={categories}
              selectedCategories={selectedCategories}
              onChange={setSelectedCategories}
            />

            <ExpoGrid
              exhibitors={filteredExhibitors}
              viewMode={viewMode}
              onExhibitorClick={handleExhibitorClick}
            />
          </div>
        </div>
      </div>

      {selectedExhibitor && (
        <ExpoDetailsModal
          exhibitor={selectedExhibitor}
          onClose={() => setSelectedExhibitor(null)}
          ModalWrapper={PortalModal}
          eventSlug={eventSlug}
          onCtaTrack={() => {
            // Refresh the exhibitor to update CTA state without triggering another view event
            if (selectedExhibitor) {
              handleExhibitorClick(selectedExhibitor, true);
            }
          }}
        />
      )}
    </div>
  );
};